import { Paper } from '@mui/material';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import { Link as NavLink, Route, Switch, useHistory } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { Logger } from '../../common/Logger';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { trackAddRingGroup } from '../../common/userTracking';
import { calendarState } from '../../recoil/atoms/calendarState';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  // makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { DeleteRingGroupModal } from '../components/modals/DeleteRingGroupModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { ActionColumn } from '../modules/Manage/pages/queues-table/ActionColumns';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';
import { RingGroupSettings } from '../subpages/RingGroupSettings';

export function RingGroups() {
  const history = useHistory();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [selected, setSelected] = useState<GridRowData[]>([]);
  /**
   * Callback used by column button to delete queue
   * @param id Queue to be deleted
   */
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const setCalendarDisplay = useSetRecoilState(calendarState);
  const data = {
    call: methodNameEnum.GET_BUSINESS_GROUPS,
  };
  const ringGroups = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_GROUPS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const setRefetchToggleForBusinessGroups = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_GROUPS)
  );

  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);

  // use requester
  // const phpRequest = useMakePHPRequest({
  //   userAtom: userAtomLoadable.contents,
  // });
  const [deleteGroupId, setDeleteGroupId] = useState<
    number | string | undefined
  >('');
  // actions for opening and closing modal for add-group
  // state for detele modal
  const [showD, setShowD] = useState(false);
  const handleCloseDeleteModal = () => setShowD(false);
  const handleShowDeleteModal = () => {
    setShowD(true);
  };
  // state for notifications
  // console.log(deletedGroup, 'recoil value deleted ring group selector ');
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const ringGroupsCached = useRef([]);
  ringGroupsCached.current = useMemo(
    () =>
      ringGroups.state === 'hasValue'
        ? ringGroups.contents?.response?.data?.result
            ?.map((o: any) => ({
              ...o,
              id: o.maingroupid,
            }))
            .sort((a: any, b: any) => b.maingroupid - a.maingroupid)
        : ringGroupsCached?.current ?? [],
    [ringGroups]
  );
  // console.log(ringGroupsCached.current, 'ring groups ');
  useEffect(
    () => {
      setCalendarDisplay('none');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Callback used by column button to delete queue
   * @param id Queue to be deleted
   */
  const handleDeleteRingGroup = async (id: string | number) => {
    let errorMessage = '';

    setDeleteStatus(loadableEnum.LOADING);

    try {
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.DELETE_GROUP,
        payload: {
          call: methodNameEnum.DELETE_GROUP,
          apikey: userAtomContents.apikey,
          businessid: userAtomContents.businessid,
          groupid: id,
        },
      });

      if (response?.data?.success) {
        // clear selections list
        setDeleteGroupId(undefined);
        // refetch business queues
        setRefetchToggleForBusinessGroups({});

        // log success
        Logger.trace(
          {
            message:
              status === loadableEnum.CANCELED
                ? 'Canceled deleteRingGroup'
                : 'Success deleteRingGroup',
            response,
          },
          'RingGroups:handleDeleteRingGroup'
        );
        setDeleteStatus(loadableEnum.HAS_VALUE);
      }
    } catch (exception) {
      errorMessage = 'Failed to delete ring group';
      // log error
      Logger.error(
        {
          message: 'Failed deleteQueue',
          exception,
        },
        'RingGroups:handleDeleteRingGroup'
      );
      setDeleteStatus(loadableEnum.HAS_ERROR);
    }

    enqueueSnackbarWithStyles({
      message: errorMessage || 'Successfully deleted ring group',
      alertProps: { severity: errorMessage ? 'error' : 'success' },
    });
  };
  const readOnly = useBusinessPermissions().accountReadOnly;
  function groupNameFormatter(params: GridRenderCellParams) {
    return (
      <NavLink to={`/manage/ring-groups/${params.row.maingroupid}/edit`}>
        <p>{params.row.groupname}</p>
      </NavLink>
    );
  }
  const columns: IColDef[] = [
    {
      field: 'groupname',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: groupNameFormatter,
    },
    {
      field: 'email',
      headerName: 'Email(s)',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'hasGreeting',
      headerName: 'Greeting',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'hasRingMusic',
      headerName: 'Ring Audio',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'numberOfTiers',
      headerName: 'Tiers',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'maingroupid',
      headerName: 'Actions',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      disableExport: true,
      disableSearch: true, // do not search field content
      enableSorting: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ActionColumn({
        deleteItemId: deleteGroupId,
        setDeleteItemId: setDeleteGroupId,
        isLoading: deleteStatus === loadableEnum.LOADING,
        handleDeletion: handleDeleteRingGroup,
        editLink: '/manage/ring-groups/:id/edit',
      }),
      hide: readOnly,
    },
  ];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="RingGroups"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddRingGroup();
              history.push('/manage/ring-groups/add');
            }}
            onClickForDelete={() => {
              handleShowDeleteModal();
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [handleShowDeleteModal, selected.length, readOnly]
  );
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'groupname', sort: 'asc' },
  ]);
  return (
    <Paper square={true} elevation={0}>
      <Switch>
        <Route path="/manage/ring-groups/:id/edit">
          {({ history, match }) => (
            <RingGroupSettings
              show={match != null}
              onHide={() => {
                setRefetchToggleForBusinessGroups({});
                history.push('/manage/ring-groups');
              }}
            />
          )}
        </Route>
        <Route path="/manage/ring-groups/add">
          {({ history, match }) => (
            <RingGroupSettings
              show={match != null}
              onHide={() => {
                setRefetchToggleForBusinessGroups({});
                history.push('/manage/ring-groups');
              }}
            />
          )}
        </Route>
      </Switch>
      {ringGroups.state === 'loading' && !ringGroupsCached.current?.length ? (
        <QueuesSkeleton />
      ) : (
        <DataTable
          enableSelectAll={true}
          setSelected={setSelected}
          loading={ringGroups.state === 'loading'}
          rows={ringGroupsCached?.current ?? []}
          columns={columns}
          ToolbarComponent={ToolbarComponent}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
          selected={selected}
        />
      )}
      <DeleteRingGroupModal
        setRefetchToggleForBusinessGroups={setRefetchToggleForBusinessGroups}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        selected={selected}
        userAtom={userAtomContents}
        setUserAtom={setUserAtom}
        handleClose={handleCloseDeleteModal}
        show={showD}
      />
    </Paper>
  );
}
